import { FC, useEffect } from 'react'
import { LeadPaymentLink } from '@/api/types/crm/crm.types'
import { formatDate, formatTime } from '@pages/Admin/CRM/utils/formatData'
import { CopyOutlined, DeleteOutlined } from '@ant-design/icons'
import gclasses from '../../../../../../../CRMSettings/global.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import { useCopyToClipboard } from 'react-use'
import { useAddAlert } from '@/hooks/useAddAlert'
import { Modal as AntdModal } from 'antd'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import { isError } from '@/helpers/checkQuery'
import { crmApi } from '@/api'
import { getLeadFieldValue } from '@pages/Admin/CRM/utils/leadFields'
import { useGetExtraPurchaseFields } from '@pages/Admin/PlatformSettings2/DirectorySettings/components/ExtraPurchaseFields/utils'
import useFullLeadModalAccess from '../../../../hooks/useFullLeadModalAccess'
import type { LeadProfileLead } from '../../../../LeadProfileModal.types'
import {
  getMonthlyCourseLinkPeriodLabel,
  isMonthlyCourseLinkTariff,
} from '../CreateLinkModal/monthlyCourseLinkDiscount.utils'
import { isIndividualLessonLinkTariff } from '../CreateLinkModal/tariffTypes'

interface PurchasesLinkItemProps {
  lead: LeadProfileLead
  link: LeadPaymentLink
  refetchLinks: () => void
}

const PurchasesLinkItem: FC<PurchasesLinkItemProps> = ({
  lead,
  link,
  refetchLinks,
}) => {
  const { currentRole, ...access } = useFullLeadModalAccess()

  /* prettier-ignore */
  const item = {
    typeName: link.type === 'class' ? 'Класс' : link.type === 'course' ? !!link?.additional_courses_full?.length? 'Курсы' : 'Курс' : '',
    name: link.type === 'class' ? link.class?.title : link.type === 'course' ? link.course?.name : '-',
    statusName: link.status === 'not_used' ? 'Не использована' : link.status === 'in_process' ? 'В процессе оплаты' : 'Куплено по ссылке',
    statusColor: link.status === 'not_used' ? '#c4f6f8ff' : link.status === 'in_process' ? '#FFB800' : '#00C48C',
    funnelName: (link as any)?.funnel?.funnel_name ?? '-',
    ...link
  }

  const addAlert = useAddAlert()
  const [state, copyToClipboard] = useCopyToClipboard()
  const { extraFields } = useGetExtraPurchaseFields()

  useEffect(() => {
    if (state.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось скопировать ссылку',
        type: 'error',
      })
      console.error('Failed to copy: ', state.error)
    } else if (state.value) {
      addAlert({
        title: 'Успешно',
        text: 'Ссылка скопирована в буфер обмена',
        type: 'success',
      })
    }
  }, [state])

  const handleCopy = () => {
    const params = new URLSearchParams()

    const checkNumber = (val: any) =>
      typeof val === 'number' && !isNaN(val) && isFinite(val)

    if (checkNumber(item.id)) {
      params.append('crm_link_id', item.id.toString())
    }
    if (item.type) {
      params.append('type', item.type)
    }
    if (checkNumber(item.course_id)) {
      params.append('course_id', String(item.course_id))
    }
    if (checkNumber(item.tariff_id)) {
      params.append('tariff_id', String(item.tariff_id))
    }
    if (checkNumber(item.class_id)) {
      params.append('class_id', String(item.class_id))
    }
    if (checkNumber(item.price)) {
      params.append('price', String(item.price))
    }
    if (checkNumber(item.first_price)) {
      params.append('first_price', String(item.first_price))
    }
    if (checkNumber(item.discount)) {
      params.append('discount', String(item.discount))
    }
    if (item.enroll_date) {
      params.append('enroll_date', item.enroll_date)
    }
    if (item.additional_courses) {
      params.append('additional_courses', item.additional_courses)
    }
    if (item.is_promo_enabled) {
      params.append('is_promo_enabled', String(item.is_promo_enabled))
    }
    if (item.value) {
      params.append('value', String(item.value))
    }
    if (item.period_unit) {
      params.append('period_unit', String(item.period_unit))
    }
    if (lead.user_id) {
      params.append('user_id', String(lead.user_id))
    }

    // lead fields

    let name = getLeadFieldValue(lead, 'NAME')
    let phone = getLeadFieldValue(lead, 'PHONE')
    let email = getLeadFieldValue(lead, 'EMAIL')
    let parentName = getLeadFieldValue(lead, 'PARENTNAME')
    let parentPhone = getLeadFieldValue(lead, 'PARENTPHONE')
    let parentEmail = getLeadFieldValue(lead, 'PARENTEMAIL')

    if (lead.user_id) {
      // name = lead?.user?.name
      // phone = lead.user?.phone
      // email = lead.user?.email

      // parentName = undefined
      // parentPhone = undefined
      // parentEmail = undefined

      name = undefined
      phone = undefined
      email = undefined
    }

    if (name && extraFields.includes('studentName'))
      params.append('studentName', name)
    if (phone && extraFields.includes('studentPhone'))
      params.append('studentPhone', phone)
    if (email && extraFields.includes('studentEmail'))
      params.append('studentEmail', email)
    if (parentName && extraFields.includes('parentName'))
      params.append('parentName', parentName)
    if (parentPhone && extraFields.includes('parentPhone'))
      params.append('parentPhone', parentPhone)
    if (parentEmail && extraFields.includes('parentEmail'))
      params.append('parentEmail', parentEmail)

    // handler

    const queryString = params.toString()
    const url = `${window.location.origin}/courses/all${
      queryString ? `?${queryString}` : ''
    }`

    copyToClipboard(url)
  }

  const handleDelete = () => {
    AntdModal.confirm({
      title: 'Удалить ссылку',
      content: `Вы уверены, что хотите удалить ссылку на покупку "${item.name}"?`,
      icon: null,
      centered: true,
      maskClosable: true,
      className: cn(jclasses.modal, classes.modal),
      okText: 'Подтвердить',
      cancelText: 'Отменить',
      onOk: () => {
        crmApi
          .deleteLeadPaymentLink(item.id)
          .then((res) => {
            if (isError(res)) throw new Error()
            refetchLinks()
            addAlert({
              title: 'Успешно',
              text: 'Ссылка на покупку успешно удалена',
              type: 'success',
            })
          })
          .catch((error) => {
            console.error('Ошибка при удалении ссылки:', error)
            addAlert({
              title: 'Ошибка',
              text: 'Не удалось удалить ссылку на покупку',
              type: 'error',
            })
          })
      },
      onCancel: () => {},
    })
  }

  console.log(item)

  return (
    <div className={cn(classes.item)}>
      <div className={classes.itemSectionOne}>
        <div className={classes.itemDate}>
          <span className={classes.bold}>{formatDate(link.created_at)}</span>
          <span>{formatTime(link.created_at)}</span>
        </div>
        <div className={classes.itemContent}>
          <div className={classes.alignRightWrapper}>
            <div className={classes.tag}>{item.typeName}</div>
          </div>
          <p className={classes.itemName}>
            {[
              item.name,
              ...(item?.additional_courses_full?.map(
                (c) => c?.course?.name ?? c?.course?.short_name ?? '-',
              ) || []),
            ].join(', ')}
          </p>
        </div>
      </div>
      <div className={classes.itemSectionTwo}>
        {!!item.tariff_id && (
          <div
            className={classes.tagSecondary}
          >{`Тариф: ${item.tariff?.name}`}</div>
        )}
        {item.group_id != null && (
          <div className={classes.tagSecondary}>
            Группа:{' '}
            {[item.group?.name, item.group?.teacher_name]
              .filter(Boolean)
              .join(' — ') || `#${item.group_id}`}
          </div>
        )}
        {isIndividualLessonLinkTariff(item.tariff?.type) ? (
          <>
            {item.lesson_duration != null && (
              <div className={classes.tagSecondary}>
                Длительность: {item.lesson_duration} мин.
              </div>
            )}
            {item.first_price != null && (
              <div className={classes.tagSecondary}>
                Цена занятия: {item.first_price}р
              </div>
            )}
            {item.teacher_id != null && (
              <div className={classes.tagSecondary}>
                Педагог:{' '}
                {item.teacher?.name?.trim() || `#${item.teacher_id}`}
                {item.salary_per_hour != null
                  ? ` (ЗП ${item.salary_per_hour}р)`
                  : ''}
              </div>
            )}
            {!!(item.price || item.discount) && (
              <div className={classes.tagSecondary}>
                {`Цена: ${item.price}р Скидка: ${item.discount ?? 0}р`}
              </div>
            )}
          </>
        ) : (
          !!(item.price || item.discount || item.first_price) && (
            <div className={classes.tagSecondary}>
              {item.first_price ? `Первая оплата: ${item.first_price}р ` : ''}
              {`Цена: ${item.price}р Скидка: ${item.discount ?? 0}р`}
            </div>
          )
        )}
        {item.type === 'course' && isMonthlyCourseLinkTariff(item.tariff?.type) && (
          <>
            <div className={classes.tagSecondary}>
              Период: {getMonthlyCourseLinkPeriodLabel(item.period_unit, item.value)}
            </div>
            {Number(item.discount) > 0 && (
              <div className={classes.tagSecondary}>
                {item.apply_discount_each_payment
                  ? 'Скидка на каждый месяц'
                  : 'Скидка на первую оплату'}
              </div>
            )}
          </>
        )}
        {item.type === 'class' && Number(item.discount) > 0 && (
          <div className={classes.tagSecondary}>
            {item.apply_discount_each_payment
              ? 'Скидка на каждый месяц'
              : 'Скидка на первую оплату'}
          </div>
        )}
        {item.additional_courses_full?.map((course, index) => (
          <div
            key={`${course.courseId}-${course.tariffId}-${index}`}
            className={classes.tagSecondary}
          >
            Доп. курс {index + 1}:{' '}
            {course.price !== null && course.price !== undefined
              ? `${course.price}р`
              : 'цена не указана'}
            {isMonthlyCourseLinkTariff(course.tariff?.type)
              ? `, ${getMonthlyCourseLinkPeriodLabel(
                  course.period_unit,
                  course.value,
                )}`
              : ''}
            {Number(course.discount) > 0
              ? `, скидка: ${course.discount}р${
                  course.apply_discount_each_payment ? ' каждый месяц' : ''
                }`
              : ''}
          </div>
        ))}
        {item.enroll_date && (
          <div className={classes.tagSecondary}>{`Начало оплаты: ${formatDate(
            item.enroll_date,
          )}`}</div>
        )}
        <div className={classes.tagSecondary}>{`Воронка: ${item.funnelName}`}</div>
        <div className={classes.statusWrapper}>
          <div
            className={classes.statusColor}
            style={{ backgroundColor: item.statusColor }}
          />
          <p className={classes.statusName}>{item.statusName}</p>
        </div>
        <CopyOutlined
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
          className={cn(gclasses.activeIcon, classes.actionIcon)}
          onClick={handleCopy}
        />
        {access.canManagePurchaseLinks && (
          <TooltipWrapper
            placement="left"
            title={
              item.status === 'completed'
                ? 'Вы не можете удалить ссылку на покупку, так как по ней уже оплатили'
                : item.status === 'in_process'
                ? 'Вы не можете удалить ссылку на покупку, так как она в процессе оплаты'
                : ''
            }
          >
            <DeleteOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              className={cn(gclasses.activeIcon, classes.actionIcon, {
                [classes.disabledActionIcon]:
                  item.status === 'completed' || item.status === 'in_process',
              })}
              onClick={() => {
                if (
                  item.status !== 'completed' &&
                  item.status !== 'in_process'
                ) {
                  handleDelete()
                }
              }}
            />
          </TooltipWrapper>
        )}
      </div>
    </div>
  )
}

export default PurchasesLinkItem
